import type { CSSProperties } from 'react';
import { PlushIllustration } from './PlushIllustration.js';

export type AnimalType = 'bear' | 'rabbit' | 'dino';
export type SceneType = 'night' | 'forest' | 'party';
export type CoverBackground = 'sky' | 'sage' | 'sun' | 'terra' | 'sand';

export interface BookCoverProps {
  background?: CoverBackground;
  scene?: SceneType;
  animal?: AnimalType;
  colors?: [string, string];
  titleLine1?: string;
  titleLine2?: string;
  size?: number | string;
  aspectRatio?: string;
  style?: CSSProperties;
  showSpine?: boolean;
}

export function BookCover({
  background = 'sky',
  scene = 'night',
  animal = 'bear',
  colors,
  titleLine1,
  titleLine2,
  size = '100%',
  aspectRatio = '3/4',
  style,
  showSpine = false,
}: BookCoverProps) {
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        aspectRatio,
        borderRadius: '4px 10px 10px 4px',
        overflow: 'hidden',
        boxShadow: '0 14px 30px -16px rgba(80,55,20,0.3)',
        ...style,
      }}
    >
      <div
        className={`book-illus ${background}`}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '8%',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 9,
            letterSpacing: 2,
            color: 'rgba(44,36,24,0.55)',
          }}
        >
          EIN PERSÖNLICHES BUCH
        </div>

        {scene === 'night' && (
          <>
            <div
              style={{
                position: 'absolute',
                right: '14%',
                top: '14%',
                width: '15%',
                aspectRatio: '1',
                borderRadius: '50%',
                background: '#F8EBB7',
                boxShadow: 'inset -5px -3px 0 rgba(180,140,60,0.2)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '15%',
                top: '22%',
                width: 6,
                height: 6,
                background: '#fff',
                clipPath: starClip,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '32%',
                top: '36%',
                width: 4,
                height: 4,
                background: '#fff',
                clipPath: starClip,
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '28%',
                top: '40%',
                width: 5,
                height: 5,
                background: '#fff',
                clipPath: starClip,
              }}
            />
          </>
        )}
        {scene === 'forest' && (
          <>
            <div
              style={{
                position: 'absolute',
                left: '10%',
                bottom: '32%',
                width: 0,
                height: 0,
                borderLeft: '20px solid transparent',
                borderRight: '20px solid transparent',
                borderBottom: '50px solid #6B8F6B',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '12%',
                bottom: '36%',
                width: 0,
                height: 0,
                borderLeft: '16px solid transparent',
                borderRight: '16px solid transparent',
                borderBottom: '40px solid #5C7E5A',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '32%',
                bottom: '32%',
                width: 0,
                height: 0,
                borderLeft: '14px solid transparent',
                borderRight: '14px solid transparent',
                borderBottom: '34px solid #6B8F6B',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '15%',
                top: '12%',
                width: '13%',
                aspectRatio: '1',
                borderRadius: '50%',
                background: '#F8D77A',
              }}
            />
          </>
        )}
        {scene === 'party' && (
          <>
            <div
              style={{
                position: 'absolute',
                left: '14%',
                top: '15%',
                width: 6,
                height: 10,
                background: '#E89B6F',
                transform: 'rotate(20deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '32%',
                top: '24%',
                width: 6,
                height: 10,
                background: '#7B9F6F',
                transform: 'rotate(-30deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '20%',
                top: '18%',
                width: 6,
                height: 10,
                background: '#D4936A',
                transform: 'rotate(45deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '38%',
                top: '32%',
                width: 6,
                height: 10,
                background: '#A8B47E',
                transform: 'rotate(15deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '14%',
                top: '32%',
                width: '11%',
                height: '14%',
                borderRadius: '50%',
                background: '#E89B6F',
              }}
            />
          </>
        )}

        <div
          style={{
            alignSelf: 'center',
            position: 'relative',
            width: '55%',
            zIndex: 1,
          }}
        >
          <PlushIllustration animal={animal} colors={colors} />
        </div>

        {(titleLine1 || titleLine2) && (
          <div
            style={{
              fontFamily: 'var(--serif)',
              fontWeight: 600,
              lineHeight: 1.1,
              color: 'var(--ink)',
              position: 'relative',
              zIndex: 2,
            }}
          >
            {titleLine1 && (
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 2,
                  fontWeight: 500,
                  opacity: 0.6,
                  marginBottom: 4,
                  fontFamily: 'var(--mono)',
                }}
              >
                {titleLine1.toUpperCase()}
              </div>
            )}
            {titleLine2 && <div style={{ fontSize: 19 }}>{titleLine2}</div>}
          </div>
        )}
      </div>
      {showSpine && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            background: 'linear-gradient(90deg, rgba(0,0,0,0.2), transparent)',
          }}
        />
      )}
    </div>
  );
}

const starClip =
  'polygon(50% 0,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)';
